import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

export default function AcceptInvite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') || '';

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!token) {
      toast({ title: t('acceptInvite.tokenMissing'), variant: 'destructive' });
      return;
    }

    if (password.length < 8) {
      toast({ title: t('acceptInvite.passwordMin'), variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await acceptInvite({ token, password, fullName: fullName.trim() || undefined });
      toast({ title: t('acceptInvite.welcome') });
      navigate('/dashboard');
    } catch (err) {
      toast({
        title: t('acceptInvite.failed'),
        description: err instanceof Error ? err.message : t('common.tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 space-y-5">
        <div>
          <h1 className="text-xl font-semibold">{t('acceptInvite.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('acceptInvite.subtitle')}</p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="space-y-1.5">
            <Label>{t('acceptInvite.fullName')} ({t('common.optional')})</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t('createAccount.yourName')} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('login.password')}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('acceptInvite.passwordPlaceholder')} />
          </div>

          <Button className="w-full" type="submit" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('acceptInvite.joinWorkspace')}
          </Button>
        </form>
      </div>
    </div>
  );
}
